import { CloudOff, Tags } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useAuthorPosts, type SourceInsightPostsFilters } from '../../api/authorInsights'
import type { Source } from '../../api/types'
import { setFeedOrder } from '../../lib/feedNav'
import { useViewState } from '../../lib/navigation'
import { Button } from '../Button'
import { EmptyState } from '../EmptyState'
import { FeedTextGrid, FeedVideoGrid } from '../feed/FeedGrid'
import { FeedTile, TileSkeleton } from '../feed/FeedTile'
import { TextCard, TextCardSkeleton } from '../feed/TextCard'
import { Spinner } from '../Spinner'

/** Диалог группы скрывается под постом, а его очередь листания продолжает подгружаться. */
export function AuthorFeedOrder({ sourceId, filters }: { sourceId: number; filters: SourceInsightPostsFilters }) {
  const posts = useAuthorPosts(sourceId, filters)
  const items = useMemo(() => posts.data?.pages.flatMap((page) => page.items) ?? [], [posts.data])
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = posts
  useEffect(() => {
    setFeedOrder(items.map((item) => item.id), hasNextPage && !isFetchingNextPage ? () => void fetchNextPage() : null, items)
  }, [items, hasNextPage, isFetchingNextPage, fetchNextPage])
  return null
}

export function AuthorPosts({ source, filters, enabled = true, ownsOrder = true, onAnnotation }: {
  source: Source
  filters: SourceInsightPostsFilters
  enabled?: boolean
  ownsOrder?: boolean
  onAnnotation?: (id: number) => void
}) {
  const posts = useAuthorPosts(source.id, filters, enabled)
  const [pages, setPages] = useViewState(`source.${source.id}.insight-posts.${JSON.stringify(filters)}`, 1)
  const loaded = posts.data?.pages.length ?? 0
  useEffect(() => { if (loaded > pages) setPages(loaded) }, [loaded, pages, setPages])
  useEffect(() => {
    if (enabled && loaded > 0 && loaded < pages && posts.hasNextPage && !posts.isFetching && !posts.isError) void posts.fetchNextPage()
  }, [enabled, loaded, pages, posts.hasNextPage, posts.isFetching, posts.isError, posts.fetchNextPage])
  const restoring = !posts.isError && (posts.isPending || (loaded < pages && posts.hasNextPage))
  const items = useMemo(() => posts.data?.pages.flatMap((page) => page.items) ?? [], [posts.data])
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = posts
  useEffect(() => {
    if (ownsOrder) setFeedOrder(items.map((item) => item.id), hasNextPage && !isFetchingNextPage ? () => void fetchNextPage() : null, items)
  }, [ownsOrder, items, hasNextPage, isFetchingNextPage, fetchNextPage])
  const [now] = useState(() => Date.now())
  const video = filters.format === 'video'
  const Grid = video ? FeedVideoGrid : FeedTextGrid
  return <div data-view-ready={restoring ? 'false' : 'true'}>
    {posts.isPending ? <Grid>{Array.from({ length: video ? 10 : 4 }, (_, index) => video ? <TileSkeleton key={index} /> : <TextCardSkeleton key={index} />)}</Grid>
      : posts.isError && !posts.data ? <EmptyState icon={CloudOff} title="Не удалось загрузить публикации"><Button className="mt-3" onClick={() => void posts.refetch()}>Повторить</Button></EmptyState>
        : items.length === 0 ? <p className="rounded-xl bg-surface/60 p-4 text-sm text-muted">{filters.selection === 'below' ? 'В этом срезе нет надёжно оценённых публикаций ниже обычного уровня.' : filters.selection === 'best' ? 'Недостаточно сопоставимых оценок для списка лучших публикаций.' : 'В этом срезе сохранённых публикаций пока нет.'}</p>
          : <>
            {posts.isRefetchError && <p className="mb-3 text-xs text-danger">Не удалось обновить публикации. Показана последняя загруженная версия. <button className="underline" onClick={() => void posts.refetch()}>Повторить</button></p>}
            <Grid>{items.map((item) => <div key={item.id} className="min-w-0">
              {video ? <FeedTile item={item} now={now} isNew={false} /> : <TextCard item={item} now={now} isNew={false} />}
              {onAnnotation && <Button size="sm" variant="ghost" className="mt-1 w-full" onClick={() => onAnnotation(item.id)}><Tags size={12} />Разметка</Button>}
            </div>)}</Grid>
            <div className="mt-5 flex flex-col items-center gap-2">
              <p className="text-xs text-muted">Показано публикаций: {items.length}</p>
              {posts.isFetchNextPageError && <p className="text-sm text-danger">Не удалось загрузить следующую страницу.</p>}
              {posts.hasNextPage && <Button disabled={posts.isFetchingNextPage} onClick={() => void posts.fetchNextPage()}>{posts.isFetchingNextPage && <Spinner size={14} />}Показать ещё</Button>}
            </div>
          </>}
  </div>
}
